import { z } from 'zod'

// Helpers + constants for SsoConnectionFormFields. Lives in a
// separate file (rather than alongside the component) so the
// component file is component-only and Vite's `react-refresh`
// can hot-reload cleanly. Same module otherwise — the parent pages
// import from this file directly.

/**
 * Connection-ID slug shape — shared between Create + Onboard pages.
 *
 * Tighter than the loose `/^[a-z0-9-]+$/` regex on both sides
 * pre-fix: that allowed `--foo`, `foo--bar`, leading `-foo`, and
 * trailing `foo-`. None of these are useful slugs (the slug becomes
 * part of the public login URL `/sso/saml/<slug>/login`), they
 * read as typos, and they degrade to an inscrutable URL the moment
 * a browser percent-encodes anything.
 *
 * Shape:
 *   - one or more `[a-z0-9]` characters
 *   - optionally followed by `-` + more `[a-z0-9]` runs (zero or
 *     more times)
 * In practice: lowercase ASCII alphanumerics, single hyphens
 * between alphanumeric runs only — the canonical "kebab-case"
 * shape every existing connection slug already follows.
 *
 * Per-page schemas import this rather than duplicating the regex
 * so a future tightening only touches one site. The error message
 * is consolidated here too — phrasing is what the staff sees in
 * the form's helper-text caption when validation trips.
 */
export const CONNECTION_ID_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
export const CONNECTION_ID_ERROR =
    'Use lowercase letters, digits, and single hyphens between them (no leading, trailing, or repeated hyphens).'

export const NAMEID_FORMATS = [
    {
        value: 'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent',
        label: 'persistent (recommended for stable per-user matching)',
    },
    {
        value: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
        label: 'emailAddress',
    },
    {
        value: 'urn:oasis:names:tc:SAML:2.0:nameid-format:transient',
        label: 'transient',
    },
    {
        value: 'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified',
        label: 'unspecified',
    },
] as const

// Default attribute map: the Shibboleth-shaped names most
// deployments ship with (`email` / `givenName` / `sn`), used to
// seed the form. The three canonical keys (email, first_name,
// last_name) are editable per-connection in the "Attribute
// mapping (advanced)" section — those inputs accept any attribute
// name, including the OID-style URIs InCommon / eduGAIN / GakuNin
// IdPs emit, and `attributeMapFromFields` reassembles them into
// `attribute_map` on submit. A general key-value editor for maps
// with keys beyond these three is a future iteration.
export const DEFAULT_ATTRIBUTE_MAP: Record<string, string> = {
    email: 'email',
    first_name: 'givenName',
    last_name: 'sn',
}

// Email-domain entries: lowercase, trim, drop empties, dedupe in
// the same shape habitat-admin's normalizeEmailDomains expects on
// the way in. Empty list is allowed at create time.
export function parseEmailDomains(raw: string): string[] {
    const seen = new Set<string>()
    const out: string[] = []
    for (const piece of raw.split(/[,\s]+/)) {
        const clean = piece.trim().toLowerCase()
        if (!clean) continue
        if (seen.has(clean)) continue
        seen.add(clean)
        out.push(clean)
    }
    return out
}

/**
 * Validate that a single string looks like a plausible DNS domain:
 *   - 1+ labels separated by `.` (so "localhost" is rejected — we
 *     want at least one TLD-style boundary)
 *   - each label is `[a-z0-9]` optionally with `[a-z0-9-]` in the
 *     middle (no leading/trailing hyphen), 1-63 chars
 *   - whole thing is at most 253 chars
 *   - no `@`, no whitespace, no `..`
 *
 * Returns an error message on failure or `null` on success. The
 * regex deliberately accepts ASCII-only — IDN punycode (`xn--…`)
 * passes because the prefix is all ASCII; non-punycoded Unicode
 * domains are rejected at the client (they'd also fail
 * server-side normalization). The user-facing error nudges them to
 * punycode the value before pasting.
 *
 * Returning a string-or-null shape keeps callers terse:
 *   `const err = validateEmailDomain(d); if (err) return err`
 */
export function validateEmailDomain(domain: string): string | null {
    if (!domain) return 'Domain is empty.'
    if (domain.length > 253)
        return `Domain '${domain}' is too long (max 253 chars).`
    if (domain.includes('@')) return `Domain '${domain}' must not contain '@'.`
    if (/\s/.test(domain)) return `Domain '${domain}' contains whitespace.`
    if (domain.includes('..'))
        return `Domain '${domain}' has an empty label ('..').`
    // RFC 1035-ish label shape, with at least one dot.
    const LABEL = '[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?'
    const re = new RegExp(`^${LABEL}(?:\\.${LABEL})+$`)
    if (!re.test(domain)) {
        return `Domain '${domain}' is not a valid hostname (use punycode for non-ASCII labels).`
    }
    return null
}

/**
 * Validate the whole `email_domains_raw` text field. Splits via
 * `parseEmailDomains` and runs each through `validateEmailDomain`.
 * Returns the first error message, or `null` if every parsed entry
 * is valid (including the all-empty case — empty list is permitted
 * at create time).
 */
export function validateEmailDomainsRaw(
    raw: string | undefined
): string | null {
    if (!raw) return null
    for (const domain of parseEmailDomains(raw)) {
        const err = validateEmailDomain(domain)
        if (err) return err
    }
    return null
}

// Three input modes for IdP configuration:
//   - triple        : staff types entity ID, SSO URL, and signing cert PEM directly
//   - metadata_url  : staff supplies a metadata URL; the still-pending habitat-admin
//                     metadata-ingestion cron (separate Phase 2 item) will refresh
//                     the cached cert from it. Entity ID + SSO URL still typed
//                     manually until the cron lands.
//   - metadata_xml  : staff pastes the full metadata XML. Same caveat as above —
//                     entity ID + SSO URL still typed manually for v1.
export type SsoInputMode = 'triple' | 'metadata_url' | 'metadata_xml'

export const INPUT_MODES: { value: SsoInputMode; label: string }[] = [
    { value: 'triple', label: 'Entity ID + SSO URL + Cert' },
    { value: 'metadata_url', label: 'Metadata URL' },
    { value: 'metadata_xml', label: 'Metadata XML' },
]

// Schema for the shared fields only. Parents add `org_id` /
// `connection_id` rules of their own. Mirrors habitat-admin's
// createBody (where `idp_metadata_url`, `idp_metadata_xml`, and
// `idp_signing_cert` are all `nullish`) — we make exactly one
// required client-side based on `input_mode` via the
// `ssoModeRefine` function below.
//
// Object + refine are exported separately so parents can compose
// them. `.superRefine()` returns `ZodEffects`, which is not
// `.merge()`-able with another `ZodObject`. Parents that need
// page-level fields (e.g. the Create page's `org_id` +
// `connection_id`) merge `ssoFieldsObjectSchema` into their own
// page schema first, then apply `ssoModeRefine` on the merged
// result. Parents that don't need extras (e.g. the Edit page) can
// reach for `ssoFieldsSchema`, which has the refine pre-applied.
export const ssoFieldsObjectSchema = z.object({
    input_mode: z.enum(['triple', 'metadata_url', 'metadata_xml']),
    idp_entity_id: z
        .string()
        .min(1, 'IdP entity ID is required.')
        .max(1024, 'IdP entity ID is too long.'),
    idp_sso_url: z
        .string()
        .min(1, 'IdP SSO URL is required.')
        .url(
            'Must be a fully-qualified URL (e.g. https://idp.example.edu/...).'
        )
        .max(1024, 'IdP SSO URL is too long.'),
    idp_signing_cert: z.string().max(65535).optional(),
    idp_metadata_url: z.string().max(1024).optional(),
    idp_metadata_xml: z.string().max(1_000_000).optional(),
    nameid_format: z.string().max(128),
    // Optional UPN-attribute name. Empty/blank string normalises to
    // null on submit (= "use email-attribute domain for the gate";
    // pre-2026-05-03 behaviour). Set to the IdP's UPN-carrying
    // attribute name for AD/Azure AD-style federations where the
    // `email` attribute carries a different domain than the user
    // logs in with. Trimmed on the submit boundary; the form keeps
    // the raw value during editing.
    username_attribute: z.string().max(128).optional(),
    // SP→IdP attribute-name map. The SP reads the user's `email`,
    // `first_name`, `last_name` from the IdP attribute whose NAME is set
    // here (see hl-accounts `mapProfileAttributes`). Defaults are the
    // Shibboleth/LDAP names (`email` / `givenName` / `sn`); Azure AD and
    // others use different names — e.g. the `…/claims/emailaddress` URI, or
    // a short `email` claim added IdP-side. A blank `email` falls back to
    // the SP's built-in `email`/`mail` lookup. Assembled into `attribute_map`
    // on submit via `attributeMapFromFields`.
    attribute_email: z.string().max(128),
    attribute_first_name: z.string().max(128),
    attribute_last_name: z.string().max(128),
    email_domains_raw: z
        .string()
        .optional()
        .refine(
            (raw) => validateEmailDomainsRaw(raw) === null,
            (raw) => ({
                message: validateEmailDomainsRaw(raw) ?? 'Invalid domain.',
            })
        ),
    allow_idp_initiated: z.boolean(),
    enforce_sso: z.boolean(),
    enabled: z.boolean(),
    require_response_signature: z.boolean(),
})

// Mode-specific required-field check. Cast `data` is wide so this
// can be applied to schemas that augment the shared object with
// page-level fields (Create page's `org_id` + `connection_id`).
export function ssoModeRefine(
    data: { input_mode: SsoInputMode } & Partial<{
        idp_signing_cert: string
        idp_metadata_url: string
        idp_metadata_xml: string
    }>,
    ctx: z.RefinementCtx
) {
    if (data.input_mode === 'triple') {
        if (!data.idp_signing_cert || data.idp_signing_cert.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['idp_signing_cert'],
                message: 'Paste the IdP signing certificate (PEM body).',
            })
        }
        return
    }
    if (data.input_mode === 'metadata_url') {
        if (!data.idp_metadata_url || data.idp_metadata_url.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['idp_metadata_url'],
                message: 'Paste the metadata URL.',
            })
            return
        }
        // habitat-admin requires a fully-qualified URL on the
        // server side; surface that early.
        try {
            new URL(data.idp_metadata_url)
        } catch {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['idp_metadata_url'],
                message:
                    'Must be a fully-qualified URL (e.g. https://idp.example.edu/idp/shibboleth).',
            })
        }
        return
    }
    if (data.input_mode === 'metadata_xml') {
        if (!data.idp_metadata_xml || data.idp_metadata_xml.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['idp_metadata_xml'],
                message:
                    'Paste the full metadata XML (the <md:EntityDescriptor> element).',
            })
        }
    }
}

// Convenience export for parents that don't need extra page-level
// fields (e.g. the Edit page validates against this directly).
export const ssoFieldsSchema = ssoFieldsObjectSchema.superRefine(ssoModeRefine)

export type SsoFieldsValues = z.infer<typeof ssoFieldsObjectSchema>

export const defaultSsoFieldsValues = (): SsoFieldsValues => ({
    input_mode: 'triple',
    idp_entity_id: '',
    idp_sso_url: '',
    idp_signing_cert: '',
    idp_metadata_url: '',
    idp_metadata_xml: '',
    nameid_format: NAMEID_FORMATS[0].value,
    username_attribute: '',
    attribute_email: DEFAULT_ATTRIBUTE_MAP.email,
    attribute_first_name: DEFAULT_ATTRIBUTE_MAP.first_name,
    attribute_last_name: DEFAULT_ATTRIBUTE_MAP.last_name,
    email_domains_raw: '',
    allow_idp_initiated: false,
    enforce_sso: false,
    enabled: false,
    require_response_signature: false,
})

/**
 * Assemble the three attribute-name form fields into the `attribute_map`
 * record habitat-admin stores (the shape hl-accounts' `mapProfileAttributes`
 * reads). Trimmed; empty values are kept verbatim — the SP tolerates a blank
 * mapping and falls back to its built-in lookups.
 */
export function attributeMapFromFields(values: {
    attribute_email: string
    attribute_first_name: string
    attribute_last_name: string
}): Record<string, string> {
    return {
        email: values.attribute_email.trim(),
        first_name: values.attribute_first_name.trim(),
        last_name: values.attribute_last_name.trim(),
    }
}

/**
 * Inverse of `attributeMapFromFields` — populate the form's attribute fields
 * from a loaded connection's `attribute_map`, falling back to the Shibboleth
 * defaults for any key the stored row didn't carry.
 */
export function attributeFieldsFromMap(
    map: Record<string, string> | null | undefined
): {
    attribute_email: string
    attribute_first_name: string
    attribute_last_name: string
} {
    return {
        attribute_email: map?.email ?? DEFAULT_ATTRIBUTE_MAP.email,
        attribute_first_name:
            map?.first_name ?? DEFAULT_ATTRIBUTE_MAP.first_name,
        attribute_last_name: map?.last_name ?? DEFAULT_ATTRIBUTE_MAP.last_name,
    }
}

/**
 * Normalise the form's `username_attribute` into the value habitat-admin
 * stores. Form keeps a string for editing convenience; the model column
 * is nullable. Empty / whitespace-only string → null (= behave as
 * pre-2026-05-03 default: gate on email).
 */
export function normalizeUsernameAttribute(
    raw: string | undefined
): string | null {
    if (!raw) return null
    const trimmed = raw.trim()
    return trimmed.length > 0 ? trimmed : null
}

// Derive the input mode from a loaded row (for the Edit page).
// Priority: metadata_xml > metadata_url > triple. Matches the
// "more-explicit-source-wins" heuristic — if both metadata_xml
// and metadata_url are set, the XML is the more authoritative
// source and we surface that tab to the staff editor.
export function detectInputMode(row: {
    idp_metadata_xml?: string | null
    idp_metadata_url?: string | null
}): SsoInputMode {
    if (row.idp_metadata_xml && row.idp_metadata_xml.length > 0) {
        return 'metadata_xml'
    }
    if (row.idp_metadata_url && row.idp_metadata_url.length > 0) {
        return 'metadata_url'
    }
    return 'triple'
}

// Build the URL the staff click to exercise a SAML round-trip
// against an existing connection (used by the Onboard success card
// and the Edit page's "Test login" button). The IdP login endpoint
// is on hl-accounts, not staff-console — the base URL is baked into
// the bundle at build time via `VITE_HL_ACCOUNTS_BASE_URL` so the
// link points at the right origin in dev / prod.
//
// `redirect` lands on the staff-console origin's `/sso/callback`,
// where the existing client-side handoff page exchanges the
// one-time code for a token pair. Using `window.location.origin`
// means the link always lands back on whichever staff-console host
// the staff is using — local laptop, staging, prod — without
// hard-coding it.

export const HL_ACCOUNTS_UNCONFIGURED_MESSAGE =
    'Test login is unavailable — VITE_HL_ACCOUNTS_BASE_URL is not configured for this build.'

// Resolve the hl-accounts origin the Test-login link points at.
// Baked into the bundle at build time via `VITE_HL_ACCOUNTS_BASE_URL`.
// Returns `null` when it's unset in a non-dev build, so callers can
// disable the Test-login affordance and surface a clear message
// instead of handing the operator a link to localhost that goes
// nowhere on staging/prod. In dev we still fall back to
// `localhost:3000` so the local rig works with no extra config.
export function resolveHlAccountsBaseUrl(): string | null {
    const configured = import.meta.env.VITE_HL_ACCOUNTS_BASE_URL
    if (configured) return configured
    return import.meta.env.DEV ? 'http://localhost:3000' : null
}

// Throws when the base URL is unconfigured (non-dev build with no
// env var). Callers should gate on `resolveHlAccountsBaseUrl()` and
// only reach this when it returns a value — the throw is a defensive
// backstop, not the primary UX path.
export function buildSsoTestLoginUrl(connectionId: string): string {
    const baseUrl = resolveHlAccountsBaseUrl()
    if (!baseUrl) {
        throw new Error(HL_ACCOUNTS_UNCONFIGURED_MESSAGE)
    }
    const callback = `${window.location.origin}/sso/callback`
    return `${baseUrl}/sso/saml/${encodeURIComponent(
        connectionId
    )}/login?redirect=${encodeURIComponent(callback)}`
}

// Build the mode-specific portion of the create / update payload.
// The non-active mode's columns are NULLed so toggling modes is a
// clean swap rather than an accumulating set of stale columns.
export function payloadForMode(
    values: Pick<
        SsoFieldsValues,
        | 'input_mode'
        | 'idp_signing_cert'
        | 'idp_metadata_url'
        | 'idp_metadata_xml'
    >
): {
    idp_signing_cert: string | null
    idp_metadata_url: string | null
    idp_metadata_xml: string | null
} {
    switch (values.input_mode) {
        case 'triple':
            return {
                idp_signing_cert: values.idp_signing_cert ?? null,
                idp_metadata_url: null,
                idp_metadata_xml: null,
            }
        case 'metadata_url':
            return {
                idp_signing_cert: null,
                idp_metadata_url: values.idp_metadata_url ?? null,
                idp_metadata_xml: null,
            }
        case 'metadata_xml':
            return {
                idp_signing_cert: null,
                idp_metadata_url: null,
                idp_metadata_xml: values.idp_metadata_xml ?? null,
            }
    }
}
